
import _ from 'lodash';

import { MHTrackErrors } from '../lib-meet-hour';
import { toState } from '../redux';
import { appendURLParam, getBackendSafePath, readRuntimeForceRelayFlag, safeDecodeURIComponent } from '../util';

import {
    AVATAR_URL_COMMAND,
    EMAIL_COMMAND,
    MH_CONFERENCE_URL_KEY
} from './constants';
import logger from './logger';

/**
 * Returns root conference state.
 *
 * @param {Object} state - Global state.
 * @returns {Object} Conference state.
 */
export const getConferenceState = (state: Object) => state['features/base/conference'];


/**
 * Attach a set of local tracks to a conference.
 *
 * @param {MHConference} conference - Conference instance.
 * @param {MHLocalTrack[]} localTracks - List of local media tracks.
 * @protected
 * @returns {Promise}
 */
export function _addLocalTracksToConference(
        conference: { addTrack: Function, getLocalTracks: Function },
        localTracks: Array<Object>) {
    const conferenceLocalTracks = conference.getLocalTracks();
    const promises = [];

    for (const track of localTracks) {
        // XXX The library lib-meet-hour may be draconian, for example, when
        // adding one and the same video track multiple times.
        const alreadyAdded = conferenceLocalTracks.indexOf(track) !== -1;

        // Skip tracks that have already been disposed (e.g. a device switch or a
        // reconnect disposed the track before this add ran, or the track was
        // still in redux state at CONFERENCE_WILL_JOIN time). MHConference#addTrack
        // delegates to replaceTrack, which rejects such tracks with
        // `track.track_is_disposed`, leaving the local participant publishing no
        // media. Note that lib-meet-hour exposes this as the `disposed` boolean on
        if (track.disposed) {
            logger.warn(`Skipping add of an already disposed local track ${track}`);
        } else if (!alreadyAdded) {
            promises.push(
                conference.addTrack(track).catch(err => {
                    // The track can be disposed while the add is still in flight;
                    // the check above only covers the synchronous case. That is a
                    // benign race (the track is gone either way), so it is logged
                    // rather than reported as an error -- same as the symmetric
                    // handling in _removeLocalTracksFromConference.
                    if (err.name === MHTrackErrors.TRACK_IS_DISPOSED) {
                        logger.warn(
                            `Local track was disposed while being added to the conference ${track}`);

                        return;
                    }

                    _reportError(
                        'Failed to add local track to conference',
                        err);
                }));
        }
    }

    return Promise.all(promises);
}

/**
 * Logic shared between web and RN which processes the {@code USER_JOINED}
 * conference event and dispatches either {@link participantJoined} or
 * {@link hiddenParticipantJoined}.
 *
 * @param {Object} store - The redux store.
 * @param {MeetHourConference} conference - The conference for which the
 * {@code USER_JOINED} event is being processed.
 * @param {MHParticipant} user - The user who has just joined.
 * @returns {void}
 */
export function commonUserJoinedHandling(
        { dispatch }: Object,
        conference: Object,
        user: Object) {
    const id = user.getId();
    const displayName = user.getDisplayName();

    const { hiddenParticipantJoined, participantJoined } = require('../participants/actions');

    if (user.isHidden()) {
        dispatch(hiddenParticipantJoined(id, displayName));
    } else {
        dispatch(participantJoined({
            botType: user.getBotType(),
            connectionStatus: user.getConnectionStatus(),
            conference,
            deviceLabel: user.getDeviceLabel?.(),
            id,
            name: displayName,
            persistentUserId: user.getPersistentUserId?.(),
            presence: user.getStatus(),
            role: user.getRole(),
            sessionId: user.getSessionId?.()
        }));
    }
}

/**
 * Logic shared between web and RN which processes the {@code USER_LEFT}
 * conference event and dispatches either {@link participantLeft} or
 * {@link hiddenParticipantLeft}.
 *
 * @param {Object} store - The redux store.
 * @param {MeetHourConference} conference - The conference for which the
 * {@code USER_LEFT} event is being processed.
 * @param {MHParticipant} user - The user who has just left.
 * @returns {void}
 */
export function commonUserLeftHandling(
        { dispatch }: Object,
        conference: Object,
        user: Object) {
    const id = user.getId();

    const { hiddenParticipantLeft, participantLeft } = require('../participants/actions');

    if (user.isHidden()) {
        dispatch(hiddenParticipantLeft(id));
    } else {
        dispatch(participantLeft(id, conference));
    }
}

/**
 * Evaluates a specific predicate for each {@link MHConference} known to the
 * redux state features/base/conference while it returns {@code true}.
 *
 * @param {Function | Object} stateful - The redux store, state, or
 * {@code getState} function.
 * @param {Function} predicate - The predicate to evaluate for each
 * {@code MHConference} know to the redux state features/base/conference
 * while it returns {@code true}.
 * @returns {boolean} If the specified {@code predicate} returned {@code true}
 * for all {@code MHConference} instances known to the redux state
 * features/base/conference.
 */
export function forEachConference(
        stateful: Function | Object,
        predicate: (Object, URL) => boolean) {
    const state = toState(stateful)['features/base/conference'];

    for (const v of Object.values(state)) {
        // Does the value of the base/conference's property look like a
        // MHConference?
        if (v && typeof v === 'object') {
            // $FlowFixMe
            const url: URL = v[MH_CONFERENCE_URL_KEY];

            // XXX The Web version of Meet Hour does not utilize
            // MH_CONFERENCE_URL_KEY at the time of this writing. An
            // alternative is necessary then to recognize MHConference
            // instances and myUserId is as good as any other property.
            if ((url || typeof v.myUserId === 'function')
                    && !predicate(v, url)) {
                return false;
            }
        }
    }

    return true;
}

/**
 * Returns the display name of the conference.
 *
 * @param {Function | Object} stateful - Reference that can be resolved to Redux
 * state with the {@code toState} function.
 * @returns {string}
 */
export function getConferenceName(stateful: Function | Object): string {
    const state = toState(stateful);
    const { callee } = state['features/base/jwt'];
    const { callDisplayName } = state['features/base/config'];
    const { pendingSubjectChange, room, subject } = state['features/base/conference'];

    return pendingSubjectChange
        || subject
        || callDisplayName
        || (callee && callee.name)
        || safeStartCase(safeDecodeURIComponent(room));
}

/**
 * Returns the name of the conference formated for the title.
 *
 * @param {Function | Object} stateful - Reference that can be resolved to Redux state with the {@code toState}
 * function.
 * @returns {string} - The name of the conference formated for the title.
 */
export function getConferenceNameForTitle(stateful: Function | Object) {
    return safeStartCase(safeDecodeURIComponent(toState(stateful)['features/base/conference'].room));
}

/**
* Returns the UTC timestamp when the first participant joined the conference.
*
* @param {Function | Object} stateful - Reference that can be resolved to Redux
* state with the {@code toState} function.
* @returns {number}
*/
export function getConferenceTimestamp(stateful: Function | Object): number {
    const state = toState(stateful);
    const { conferenceTimestamp } = state['features/base/conference'];

    return conferenceTimestamp;
}

/**
 * Returns the current {@code MHConference} which is joining or joined and is
 * not leaving. Please note the contrast with merely reading the
 * {@code conference} state of the feature base/conference which is not joining
 * but may be leaving already.
 *
 * @param {Function|Object} stateful - The redux store, state, or
 * {@code getState} function.
 * @returns {MHConference|undefined}
 */
export function getCurrentConference(stateful: Function | Object) {
    const { conference, joining, leaving, membersOnly, passwordRequired }
        = toState(stateful)['features/base/conference'];

    // There is a precendence
    if (conference) {
        return conference === leaving ? undefined : conference;
    }

    return joining || passwordRequired || membersOnly;
}

/**
 * Returns the stored room name.
 *
 * @param {Object} state - The current state of the app.
 * @returns {string}
 */
export function getRoomName(state: Object): string {
    return state['features/base/conference'].room;
}

/**
 * Handle an error thrown by the backend (i.e. {@code lib-meet-hour}) while
 * manipulating a conference participant (e.g. Pin or select participant).
 *
 * @param {Error} err - The Error which was thrown by the backend while
 * manipulating a conference participant and which is to be handled.
 * @protected
 * @returns {void}
 */
export function _handleParticipantError(err: { message: ?string }) {
    // XXX DataChannels are initialized at some later point when the conference
    // has multiple participants, but code that pins or selects a participant
    // might be executed before. This is a benign race condition that only occurs
    // in single-user calls. Here we're swallowing this particular expected error.
    // TODO lib-meet-hour should be fixed to not throw such an exception in
    // these scenarios.
    const channelDisabledMessages = [
        'Data channels support is disabled!',
        'Channel support is disabled!' // Alternative message format
    ];

    if (!channelDisabledMessages.includes(err.message)) {
        throw err;
    }
}

/**
 * Determines whether a specific string is a valid room name.
 *
 * @param {(string|undefined)} room - The name of the conference room to check
 * for validity.
 * @returns {boolean} If the specified room name is valid, then true; otherwise,
 * false.
 */
export function isRoomValid(room: ?string) {
    return typeof room === 'string' && room !== '';
}

/**
 * Remove a set of local tracks from a conference.
 *
 * @param {MHConference} conference - Conference instance.
 * @param {MHLocalTrack[]} localTracks - List of local media tracks.
 * @protected
 * @returns {Promise}
 */
export function _removeLocalTracksFromConference(
        conference: { removeTrack: Function },
        localTracks: Array<Object>) {
    return Promise.all(localTracks.map(track =>
        conference.removeTrack(track)
            .catch(err => {
                // Local track might be already disposed by direct
                // MHTrack#dispose() call. So we should ignore this error
                // here.
                if (err.name !== MHTrackErrors.TRACK_IS_DISPOSED) {
                    _reportError(
                        'Failed to remove local track from conference',
                        err);
                }
            })
    ));
}

/**
 * Reports a specific Error with a specific error message. While the
 * implementation merely logs the specified msg and err via the console at the
 * time of this writing, the intention of the function is to abstract the
 * reporting of errors and facilitate elaborating on it in the future.
 *
 * @param {string} msg - The error message to report.
 * @param {Error} err - The Error to report.
 * @private
 * @returns {void}
 */
function _reportError(msg, err) {
    // TODO This is a good point to call some global error handler when we have
    // one.
    logger.error(msg, err);
}

/**
 * Sends a representation of the local participant such as her avatar (URL),
 * e-mail address, and display name to (the remote participants of) a specific
 * conference.
 *
 * @param {Function|Object} stateful - The redux store, state, or
 * {@code getState} function.
 * @param {MHConference} conference - The {@code MHConference} to which
 * the representation of the local participant is to be sent.
 * @returns {void}
 */
export function sendLocalParticipant(
        stateful: Function | Object,
        conference: {
            sendCommand: Function,
            setDisplayName: Function,
            setLocalParticipantProperty: Function }) {
    const { getLocalParticipant } = require('../participants/functions');
    const localParticipant = getLocalParticipant(stateful);

    if (!localParticipant) {
        return;
    }

    const {
        avatarURL,
        email,
        features,
        name
    } = localParticipant;

    avatarURL && conference.sendCommand(AVATAR_URL_COMMAND, {
        value: avatarURL
    });
    email && conference.sendCommand(EMAIL_COMMAND, {
        value: email
    });

    if (features && features['screen-sharing'] === 'true') {
        conference.setLocalParticipantProperty('features_screen-sharing', true);
    }

    conference.setDisplayName(name);
}

/**
 * A safe implementation of lodash#startCase that doesn't deburr the string.
 *
 * NOTE: According to lodash roadmap, lodash v5 will have this function.
 *
 * Code based on https://github.com/lodash/lodash/blob/master/startCase.js.
 *
 * @param {string} s - The string to do start case on.
 * @returns {string}
 */
function safeStartCase(s = '') {
    return _.words(`${s}`.replace(/['\u2019]/g, '')).reduce(
        (result, word, index) => result + (index ? ' ' : '') + _.upperFirst(word)
        , '');
}


/**
 * 描述.
 *
 * @returns {any}
 */
export function inIframe() {
    try {
        return window.self !== window.top;
    } catch {
        return true;
    }
}

/**
 * Returns an object aggregating the conference options.
 *
 * @param {Object | Function} stateful - The redux store state.
 * @returns {Object} - Options object.
 */
export function getConferenceOptions(stateful: Object | Function, dispatch?: Function) {
    const state = toState(stateful);

    const config = state['features/base/config'];
    const { iAmRecorder } = config || {};
    const { locationURL } = state['features/base/connection'];
    const { defaultTranscriptionLanguage } = state['features/dynamic-branding'];

    const { tenant } = state['features/base/jwt'];
    const { getSessionPersistenceOptions } = require('../../session-persistence/functions');
    const { getLocalParticipant } = require('../participants/functions');
    const { email, name: nick } = getLocalParticipant(state) ?? {};
    const options: any = { ...config };

    options.sessionPersistence = {
        ...config.sessionPersistence,
        ...getSessionPersistenceOptions(state)
    };

    if (!iAmRecorder) {
        _applyAdaptiveTransportOptions(options);
    }

    if (tenant) {
        options.siteID = tenant;
    }

    if (options.enableDisplayNameInStats && nick) {
        options.statisticsDisplayName = nick;
    }

    if (options.enableEmailInStats && email) {
        options.statisticsId = email;
    }

    if (locationURL) {
        options.confID = `${locationURL.host}${getBackendSafePath(locationURL.pathname)}`;
    }

    options.transcriptionLanguage
        = defaultTranscriptionLanguage ?? require('../../transcribing/functions').determineTranscriptionLanguage(options, dispatch);

    // Disable analytics, if requested.
    if (options.disableThirdPartyRequests) {
        delete config.analytics?.scriptURLs;
        delete config.analytics?.amplitudeAPPKey;
    }

    return options;
}

/**
 * Applies runtime transport options based on active scenario rather than
 * static config defaults.
 *
 * Scenario rules:
 * - Automation/browser-driver sessions prefer TURN relay for stability.
 * - UDP-restricted sessions force TURN relay (relay-only ICE), so media traverses the TURN server.
 *   TURN/UDP stays available and is preferred by ICE candidate priority; TCP/TLS is used only when UDP
 *   genuinely cannot get through.
 * - P2P is never disabled here. Relay-only P2P reaches the peer over the same TURN server the JVB path
 *   would need on such a network, one hop earlier and without consuming bridge capacity. (An earlier
 *   revision keyed P2P off the participant count; that code is gone, and this function no longer reads
 *   redux state at all.)
 *
 * IMPORTANT: forcing relay must be done via {@code forceTurnRelay}
 * (iceTransportPolicy='relay'), NOT via {@code webrtcIceUdpDisable}. The bridge
 * (JVB) advertises ONLY UDP ICE candidates, and {@code webrtcIceUdpDisable}
 * strips UDP candidates from the REMOTE description too (see
 * JingleSessionPC._processNewJingleOfferIq), which would remove every JVB
 * candidate and make the connection impossible. {@code forceTurnRelay} keeps the
 * remote UDP candidates and routes them through the client's TURN relay
 * candidate over TCP/TLS, which is what actually works for UDP-blocked clients.
 *
 * @param {Object} options - Conference options to mutate.
 * @returns {void}
 */
function _applyAdaptiveTransportOptions(options) {
    const isAutomationSession = Boolean(globalThis?.navigator?.webdriver);
    const requestedUdpDisable = Boolean(options.webrtcIceUdpDisable);

    // Relay must also be forced when we have already LEARNED that this network needs it.
    //
    // _maybeApplyTransportRecoveryProfile() reacts to a real ICE failure or a closed bridge data channel
    // by writing forceTurnRelay into the config, but iceTransportPolicy is fixed when the
    // RTCPeerConnection is constructed, so that learning can only take effect on the NEXT join. This
    // function builds the options for that next join - and it used to overwrite forceTurnRelay with a
    // value derived solely from navigator.webdriver, discarding what was just learned. The result was
    // that a UDP-blocked participant retried forever with the same all-candidates policy that had just
    // failed: they would join, get no media, drop, and rejoin.
    //
    // The persisted flag survives the full page reload that the reconnect path performs, which the redux
    // config does not.
    const alreadyForcedByConfig = Boolean(options.forceTurnRelay)
        || options.p2p?.iceTransportPolicy === 'relay';
    const learnedRelayRequired = readRuntimeForceRelayFlag(options, logger);
    const shouldForceRelay
        = isAutomationSession || requestedUdpDisable || alreadyForcedByConfig || learnedRelayRequired;

    const nextP2p = {
        ...options.p2p
    };

    nextP2p.iceTransportPolicy = shouldForceRelay ? 'relay' : 'all';

    options.forceTurnRelay = shouldForceRelay;

    // TURN/UDP is the cheapest relay path and is the one that works when UDP egress is allowed but direct
    // peer reachability is not (symmetric NAT). Keep it available while relaying; only a deployment that
    // deliberately runs TCP/TLS-only TURN should turn it off, via config.useTurnUdp.
    options.useTurnUdp = options.useTurnUdp !== false;

    // Never strip remote UDP candidates: JVB is UDP-only, so disabling UDP would
    // leave no remote candidates to connect to. The relay intent is honored via
    // forceTurnRelay above.
    options.webrtcIceUdpDisable = false;

    options.p2p = nextP2p;

    if (shouldForceRelay) {
        logger.info('[Transport] forcing TURN relay for media'
            + ` (automation=${isAutomationSession} configRequested=${alreadyForcedByConfig}`
            + ` learned=${learnedRelayRequired})`);
    }
}


/**
 * Override the global config (that is, window.config) with XMPP configuration required to join as a visitor.
 *
 * @param {IStateful} stateful - The redux store state.
 * @param {string|undefined} vnode - The received parameters.
 * @param {string} focusJid - The received parameters.
 * @param {string|undefined} username - The received parameters.
 * @returns {Object}
 */
export function getVisitorOptions(stateful, vnode, focusJid, username) {
    const config = toState(stateful)['features/base/config'];

    if (!config?.hosts) {
        logger.warn('Wrong configuration, missing hosts.');

        return;
    }

    if (!vnode) {
        // this is redirecting back to main, lets restore config
        // not updating disableFocus, as if the room capacity is full the promotion to the main room will fail
        // and the visitor will be redirected back to a vnode from jicofo
        const mainConfig = _getMainConfigForVisitorPromotion(config);

        if (mainConfig) {
            // The one-time username is how prosody recognizes an individually
            // approved promotion. It is absent when the whole room stops using
            // visitor mode: nothing was approved for this person in particular,
            // the room's allow-list is simply gone and they rejoin as an ordinary
            // guest. Appending nothing is then the correct URL.
            return {
                hosts: mainConfig.hosts,
                focusUserJid: focusJid || mainConfig.focusUserJid,
                disableFocus: false,
                disableLocalStats: false,
                bosh: mainConfig.bosh
                    && (username ? appendURLParam(mainConfig.bosh, 'customusername', username) : mainConfig.bosh),
                p2p: mainConfig.p2p,
                websocket: mainConfig.websocket
                    && (username
                        ? appendURLParam(mainConfig.websocket, 'customusername', username)
                        : mainConfig.websocket),
                oldConfig: undefined // clears it up
            };
        }

        logger.warn('Cannot promote visitor back to main room; missing main config.');

        return;
    }

    const oldConfig = {
        hosts: {
            domain: ''
        },
        focusUserJid: config.focusUserJid,
        bosh: config.bosh,
        p2p: config.p2p,
        websocket: config.websocket
    };

    // copy original hosts, to make sure we do not use a modified one later
    Object.assign(oldConfig.hosts, config.hosts);

    // will have to change domain and muc only for visitor for meet hour.
    // visitorsXmppDomain comes from the server's VISITORS_XMPP_DOMAIN and must match the visitor prosody vhost.
    const domain = `${vnode}.${config.visitorsXmppDomain || 'meet.hour'}`;

    return {
        oldConfig,
        hosts: {
            domain,
            muc: config.hosts.muc.replace(oldConfig.hosts.domain, domain)
        },
        focusUserJid: focusJid,
        disableFocus: true, // This flag disables sending the initial conference request
        disableLocalStats: true,
        bosh: config.bosh && appendURLParam(config.bosh, 'vnode', vnode),
        p2p: {
            ...config.p2p,
            enabled: false
        },
        websocket: config.websocket && appendURLParam(config.websocket, 'vnode', vnode)
    };
}

/**
 * Returns the main-room XMPP config that should be used when a visitor is promoted back to the main room.
 *
 * @param {Object} currentConfig - The current config from redux, usually the visitor-node config.
 * @returns {Object|undefined}
 */
function _getMainConfigForVisitorPromotion(currentConfig) {
    if (currentConfig.oldConfig?.hosts && (currentConfig.oldConfig.bosh || currentConfig.oldConfig.websocket)) {
        return currentConfig.oldConfig;
    }

    // OVERWRITE_CONFIG updates redux but not the legacy global config object. In the web app that means
    // window.config normally still points at the original main-room config, which is a useful fallback if
    // oldConfig was lost while the visitor sat on the vnode.
    const globalConfig = typeof window === 'undefined' ? undefined : window.config;

    if (globalConfig?.hosts && (globalConfig.bosh || globalConfig.websocket)) {
        return {
            hosts: _.cloneDeep(globalConfig.hosts),
            focusUserJid: globalConfig.focusUserJid,
            bosh: globalConfig.bosh,
            p2p: _.cloneDeep(globalConfig.p2p),
            websocket: globalConfig.websocket
        };
    }
}


/**
 * Analytics may require an obfuscated room name, this functions decides based on a config if the normal or
 * obfuscated room name should be returned.
 *
 * @param {IReduxState} state - The current state of the app.
 * @param {Function} _dispatch - The Redux dispatch function (unused).
 * @returns {string} - Analytics room name.
 */
export function getAnalyticsRoomName(state, _dispatch) {
    return getRoomName(state);
}
